import { useEffect, useRef, useState } from "react";
import { Space, type TableColumnsType, theme } from "antd";

import { UserAvatar } from "@av/components/molecules/EmployeeAvatar";
import { getConsistentColorForString } from "@av/components/utilities/color-utilities";
import { getInitials } from "@av/utils/domain/user/initials-utilities";
import { EmailRetryButton } from "./EmailRetryButton";
import { EmailStatusIndicator } from "./EmailStatusIndicator";
import { type AtsAdminSettingsUser, headerCell } from "../../../../../services/admin-settings/type";
import { compareText } from "../../../../hooks/filter-admin-settings-users";
import { CommonUserTable } from "../../common/user-table/CommonUserTable";
import { createEmailColumn } from "../../common/user-table/EmailColumn";
import { createSecurityGroupColumn } from "../../common/user-table/SecurityGroupColumn";

import type { TFunction } from "i18next";

type AtsAdminSettingsUserColumnsProps = {
  t: TFunction;
  testIdPrefix: string;
  isDemo?: boolean;
  onEditUser?: (user: AtsAdminSettingsUser) => void;
  isEditUserPending?: (user: AtsAdminSettingsUser) => boolean;
  users: AtsAdminSettingsUser[];
  isLoading?: boolean;
  emailTooltipTitleKey?: string;
  emailTooltipContentKey?: string;
  securityGroupTitleKey?: string;
  /** The Users tab's design orders Email before the security group; Roles & Permissions orders the reverse. Defaults to the existing Roles & Permissions order. */
  emailBeforeSecurityGroup?: boolean;
  /** Shows whether each user was emailed when added, at the right of the security group cell. Only the Users tab shows it. */
  showEmailStatus?: boolean;
  /** Shows a "Send again" button next to a failed email status. Omit it to hide the button. */
  onRetryEmail?: (user: AtsAdminSettingsUser) => void;
  /** The user whose welcome email is being sent: their "Send again" button spins, and every row's is disabled. */
  sendingEmailUserId?: string;
};

type SortableUserField = keyof Pick<AtsAdminSettingsUser, "email" | "name">;

const pendingMessageDelayMs = 2 * 60 * 1_000;
const pendingTimeoutMs = 2 * 60 * 1_000;

function useAtsUserPendingMessage(pendingUserSignature: string, message: string) {
  const pendingSince = useRef(new Map<string, number>());
  const [longPendingUserIds, setLongPendingUserIds] = useState<Set<string>>(new Set());
  const [timedOutUserIds, setTimedOutUserIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const now = Date.now();
    const pendingUserIds = new Set(pendingUserSignature ? pendingUserSignature.split("|") : []);

    pendingUserIds.forEach((userId) => {
      pendingSince.current.set(userId, pendingSince.current.get(userId) ?? now);
    });
    pendingSince.current.forEach((_startedAt, userId) => {
      if (!pendingUserIds.has(userId)) {
        pendingSince.current.delete(userId);
      }
    });

    const updateLongPendingUsers = () => {
      const currentTime = Date.now();

      setLongPendingUserIds(
        new Set(
          [...pendingSince.current.entries()]
            .filter(([, startedAt]) => currentTime - startedAt >= pendingMessageDelayMs)
            .map(([userId]) => userId)
        )
      );
      setTimedOutUserIds(
        new Set(
          [...pendingSince.current.entries()]
            .filter(([, startedAt]) => currentTime - startedAt >= pendingTimeoutMs)
            .map(([userId]) => userId)
        )
      );
    };

    updateLongPendingUsers();

    const nextTransition = [...pendingSince.current.values()]
      .flatMap((startedAt) => [startedAt + pendingMessageDelayMs, startedAt + pendingTimeoutMs])
      .filter((transitionAt) => transitionAt > now)
      .sort((first, second) => first - second)[0];
    const timeout = nextTransition
      ? window.setTimeout(updateLongPendingUsers, nextTransition - now)
      : undefined;

    return () => {
      if (timeout !== undefined) {
        window.clearTimeout(timeout);
      }
    };
  }, [pendingUserSignature]);

  return {
    getMessage: (user: AtsAdminSettingsUser) =>
      longPendingUserIds.has(user.userId) && !timedOutUserIds.has(user.userId)
        ? message
        : undefined,
    isTimedOut: (user: AtsAdminSettingsUser) => timedOutUserIds.has(user.userId),
  };
}

function compareField(field: SortableUserField) {
  return (a: AtsAdminSettingsUser, b: AtsAdminSettingsUser) => compareText(a[field], b[field]);
}

export function UserTable(props: AtsAdminSettingsUserColumnsProps) {
  const {
    t,
    testIdPrefix,
    isDemo = false,
    onEditUser,
    isEditUserPending,
    users,
    isLoading,
    emailTooltipTitleKey,
    emailTooltipContentKey,
    securityGroupTitleKey,
    emailBeforeSecurityGroup = false,
    showEmailStatus = false,
    onRetryEmail,
    sendingEmailUserId,
  } = props;
  const { token } = theme.useToken();
  const pendingUserSignature = users
    .filter((user) => isEditUserPending?.(user))
    .map((user) => user.userId)
    .sort()
    .join("|");
  const pendingState = useAtsUserPendingMessage(
    pendingUserSignature,
    t("platform.adminSettings.users_actionMessage_atsSyncTakingLongerTooltip")
  );
  const isEditUserLongPending = (user: AtsAdminSettingsUser) =>
    pendingState.getMessage(user) !== undefined;

  const nameColumn: TableColumnsType<AtsAdminSettingsUser>[number] = {
    title: t("platform.adminSettings.users_columns_name"),
    dataIndex: "name",
    key: "name",
    defaultSortOrder: "ascend",
    sorter: compareField("name"),
    onHeaderCell: headerCell(`${testIdPrefix}-column-name`),
    width: 280,
    render: (name: string, user) => (
      <Space>
        <UserAvatar
          testIdPrefix={`${testIdPrefix}-avatar-${user.userId}`}
          info={{
            avatarUri: user.avatarUri,
            avatarColor: getConsistentColorForString(user.name),
            initials: getInitials(user.name),
          }}
          isDemo={isDemo}
        />
        <span data-testid={`${testIdPrefix}-name-${user.userId}`}>{name}</span>
      </Space>
    ),
  };

  const securityGroupColumn = createSecurityGroupColumn<AtsAdminSettingsUser>({
    t,
    productCode: "ats",
    testIdPrefix,
    titleKey: securityGroupTitleKey,
    renderSuffix: showEmailStatus
      ? (user) => (
          <Space size={4}>
            <EmailStatusIndicator
              emailStatus={user.emailStatus}
              t={t}
              testIdPrefix={testIdPrefix}
              testIdValue={user.userId}
            />
            {user.emailStatus === "FAILED" && onRetryEmail && (
              <EmailRetryButton
                t={t}
                testIdPrefix={testIdPrefix}
                testIdValue={user.userId}
                userName={user.name}
                isSending={sendingEmailUserId === user.userId}
                // One send at a time: a second send would stop the first one's toast and spinner
                disabled={sendingEmailUserId !== undefined}
                onRetry={() => onRetryEmail(user)}
              />
            )}
            {/* Keeps the Send again slot on sent rows, so every envelope lines up as in the Figma design.
              A small icon-only button is controlHeightSM wide. */}
            {user.emailStatus === "SENT" && onRetryEmail && (
              <span style={{ display: "inline-block", width: token.controlHeightSM }} />
            )}
          </Space>
        )
      : undefined,
  });

  const emailColumn = createEmailColumn<AtsAdminSettingsUser>({
    t,
    testIdPrefix,
    tooltipTitleKey: emailTooltipTitleKey,
    tooltipContentKey: emailTooltipContentKey,
  });

  const columns: TableColumnsType<AtsAdminSettingsUser> = emailBeforeSecurityGroup
    ? [nameColumn, emailColumn, securityGroupColumn]
    : [nameColumn, securityGroupColumn, emailColumn];

  return (
    <CommonUserTable
      t={t}
      dataColumns={columns}
      testIdPrefix={testIdPrefix}
      onEditUser={onEditUser}
      isEditUserPending={isEditUserPending}
      isEditUserLongPending={isEditUserLongPending}
      isEditUserTimedOut={pendingState.isTimedOut}
      editUserPendingLabel={t("platform.adminSettings.users_actionMessage_atsSyncProcessing")}
      editUserLongPendingLabel={t("platform.adminSettings.users_actionMessage_atsSyncTakingLonger")}
      editUserTimedOutLabel={t("platform.adminSettings.users_actionMessage_atsSyncTimedOut")}
      editUserPendingMessage={pendingState.getMessage}
      users={users}
      isLoading={isLoading}
    />
  );
}
